import {
  colorBackgroundDefault,
  colorBrandPrimaryDefault,
  colorOutlineDefault,
  colorStatusAlertDefault,
  colorStatusPositiveDefault,
} from '@duxton-web/design-tokens/themes/light';
import {
  CloseCircleFilled,
  QuarterLoaderUniversal,
  TickMarkFilled,
  TrashCanOutlined,
  UploadUniversal,
  WarningFilled,
} from '@duxton-web/icons';
import {
  Button,
  Flex,
  Form,
  Image,
  Spin,
  Typography,
  Upload,
  UploadFile,
} from '@duxton-web/ui';
import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearImage,
  UPLOAD_IMAGE_TO_HEDWIG,
  uploadImageToHegwig,
} from 'omega-rtc/actions/ucd';
import { SMART_CAMPAIGN_CHANNELS, SMART_CAMPAIGN_FORM_KEYS, SMART_CAMPAIGN_MAX_IMAGE_SIZE } from 'omega-rtc/constants/ucd';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';

type SmartCampaignImageUploadProps = {
  imageData: UploadFile;
  handleImageDataChange: (file: UploadFile) => void;
};
const SmartCampaignImageUpload = ({
  imageData,
  handleImageDataChange,
}: SmartCampaignImageUploadProps) => {
  const dispatch = useDispatch();
  const request = useSelector((state) => state.requests);
  const imageUpload = useSelector((state) => state.ucd.image);

  const form = Form.useFormInstance();

  const imageUploadRequest = useMemo(() => {
    return _.get(request, getRequestStateKey(UPLOAD_IMAGE_TO_HEDWIG), {});
  }, [request]);
  const isImageUploadingFailed = useMemo(
    () => imageData && imageData.status === 'error',
    [imageData],
  );

  const isImageUploadingSuccess = useMemo(
    () => imageData && imageData.status === 'done',
    [imageData],
  );

  const isPCI = useMemo(() => {
    return (
      form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL) ===
      SMART_CAMPAIGN_CHANNELS.pci
    );
  }, [form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL)]);


  useEffect(() => {
    // Clear image from redux store.
    return () => {
      clearImage();
    };
  }, []);

  useEffect(() => {
    // Update status to 'error' on upload component if error occurred.
    if (
      imageUploadRequest.isFailure &&
      imageData &&
      imageData.status === 'uploading'
    ) {
      handleImageDataChange({
        ...imageData,
        url: '',
        status: 'error',
        response: "Couldn't upload image, please try again",
      });
    }
  }, [imageUploadRequest]);

  useEffect(() => {
    if (!imageUpload || !imageData || imageData.status !== 'uploading') {
      return;
    }
    // handling image uploading success
    handleImageDataChange({
      ...imageData,
      status: 'done',
      url: imageUpload.url,
    });
  }, [imageUpload]);

  useEffect(() => {
    /**
     * resizeObserver is throwing a error in development.
     * error: 'ResizeObserver loop completed with undelivered notifications.'
     * fix:
     */
    window.ResizeObserver = undefined;
  }, []);

  const handleUpload = ({ fileList, ...info }) => {
    if (info.file && ['removed', 'error'].includes(info.file.status)) {
      return;
    }

    // Getting latest file
    const file = fileList[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      /**
       * e.target.result will contains a string  like:
       * "data:image/jpeg;base64,<base64_encoding>"
       * eg: "data:image/jpeg;base64,/9j/ul50E1JfpHT95qjbHccqSUNihiSIWaGYimWl+An4a0bxAanzoUsaGnQdNMmnHtCoTIrxNcs7V2a2DKPFV9Xxr6vHj/9k="
       */
      const base64Image = e.target.result;
      handleBase64({ base64: base64Image, file: { ...file } });
    };
    if (file.originFileObj instanceof File) {
      reader.readAsDataURL(file.originFileObj);
    }
  };

  const handleBase64 = ({ base64, file }) => {
    if (!base64 || !file) {
      return;
    }

    /**
     * (file.size > SMART_CAMPAIGN_MAX_IMAGE_SIZE * 1000) -> Is done to ensure image size is less than 250kb.
     * Note: 
     * fix.size will be total number of bytes
     * SMART_CAMPAIGN_MAX_IMAGE_SIZE * 1000 is done to convert KB to bytes.
     */

    if (file.size > SMART_CAMPAIGN_MAX_IMAGE_SIZE * 1000) {
      handleImageDataChange({
        uid: '1',
        name: file.name,
        status: 'failed',
        url: undefined,
        size: file.size,
      });
      return;
    }

    handleImageDataChange({
      uid: '1',
      name: file.name,
      status: 'uploading',
      url: undefined,
      size: file.size,
    });

    const formattedBase64Image = base64.split('base64,')[1];

    dispatch(
      uploadImageToHegwig({
        size: file.size,
        fileName: file.name,
        contentType: file.type,
        base64Str: formattedBase64Image,
        extension: file.type.split('/')[1] || file.type,
      }),
    );
  };

  const handleRemove = () => {
    handleImageDataChange(undefined);
  };

  const getActionButton = () => {
    if (!imageData) {
      return <Button size="small">Browse</Button>;
    }
    return (
      <Button icon={<TrashCanOutlined />} type="text" onClick={handleRemove} />
    );
  };

  return (
    <Upload.Dragger
      maxCount={1}
      multiple={false}
      listType="picture"
      disabled={imageUploadRequest.isPending}
      showUploadList={false}
      onChange={handleUpload}
      onRemove={handleRemove}
      beforeUpload={() => false} // To ensure no action gets triggered
      accept="image/png, image/jpeg"
      openFileDialogOnClick={!imageData}
      fileList={imageData ? [imageData] : []}
      style={{
        borderRadius: 16,
        strokeDasharray: '5 5',
        borderStyle: (isImageUploadingSuccess || isImageUploadingFailed) ? 'solid' : 'dashed',
        borderColor: isImageUploadingFailed
          ? colorStatusAlertDefault
          : colorOutlineDefault,
        backgroundColor: colorBackgroundDefault,
      }}
    >
      <Flex
        vertical={false}
        align="center"
        justify="space-between"
        style={{
          width: '100%',
          backgroundColor: colorBackgroundDefault,
        }}
      >
        <Flex gap={16} align="center">
          <div style={{ width: imageData ? 100 : 50, height: 50 }}>
            <Thumbnail file={imageData} />
          </div>

          <Flex vertical gap={4} style={{ width: 200 }} align="flex-start">
            <UploaderContentBasedOnStatus file={imageData} isPCI={isPCI} />
          </Flex>
        </Flex>

        {getActionButton()}
      </Flex>
    </Upload.Dragger>
  );
};

const Thumbnail = ({ file }) => {
  if (!file) {
    return (
      <UploadUniversal
        style={{
          margin: 10,
          fontSize: '32px',
          color: colorBrandPrimaryDefault,
        }}
      />
    );
  }

  if (file.status === 'uploading') {
    return <Spin indicator={<QuarterLoaderUniversal spin />} size="large" />;
  }

  return (
    <Image
      height={50}
      width={100}
      src={file.url}
      style={{ objectFit: 'cover' }}
      fallback="data:image/png;base64,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"
    />
  );
};

const UploaderContentBasedOnStatus = ({ file, isPCI }: { file: UploadFile, isPCI: boolean }) => {
  if (!file) {
    return (
      <React.Fragment>
        <Typography.Body level={2}>{isPCI ? 'Upload Image' : 'Upload your image'}</Typography.Body>
        <Typography.Metadata>or drag and drop here</Typography.Metadata>
      </React.Fragment>
    );
  }

  const getFileName = () => {
    return (
      <Typography.Body
        level={2}
        ellipsis={{ tooltip: file.name }}
        style={{ textAlign: 'left' }}
      >
        {file.name}
      </Typography.Body>
    );
  };

  switch (file.status) {
    case 'done': {
      return (
        <React.Fragment>
          {getFileName()}
          <Flex align="center" gap={10}>
            <TickMarkFilled
              style={{ color: colorStatusPositiveDefault, fontSize: 16 }}
            />
            <Typography.Metadata
              level={1}
              style={{ color: colorStatusPositiveDefault }}
            >
              Upload success
            </Typography.Metadata>
          </Flex>
        </React.Fragment>
      );
    }
    case 'error': {
      return (
        <React.Fragment>
          {getFileName()}
          <Flex align="center" gap={10}>
            <CloseCircleFilled
              style={{ color: colorStatusAlertDefault, fontSize: 16 }}
            />
            <Typography.Metadata
              level={1}
              style={{ color: colorStatusAlertDefault }}
            >
              Upload failed
            </Typography.Metadata>
          </Flex>
        </React.Fragment>
      );
    }
    case 'failed': {
      return (
        <React.Fragment>
          {getFileName()}
          <Flex align="center" gap={4}>
            <WarningFilled
              style={{ color: colorStatusAlertDefault, fontSize: 16 }}
            />
            <Typography.Metadata
              level={1}
              style={{ color: colorStatusAlertDefault, fontSize: 12 }}
            >
              File exceed size limit (250 KB)
            </Typography.Metadata>
          </Flex>
        </React.Fragment>
      );
    }
    default:
      return getFileName();
  }
};

export default SmartCampaignImageUpload;
